// Atlas (MUI wrap, /atlas). Page chrome only — reuses the existing canvas
// views (SemanticMap, ConstellationView, PulseView) and atlas-canvas helpers from
// pages/dashboard/atlas/. Same data/hooks as AtlasPage; kt TabRail/PageHeader/
// FilterGroup swapped for mui/* + MUI Tabs.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Tab, Tabs, Typography } from "@mui/material";
import { ScopePill } from "../../design/ui";
import { TYPE_PALETTE, typeColor } from "../../lib/type-colors";
import { getWeather } from "../../lib/brain-api";
import { useAtlasData } from "../../lib/use-atlas-data";
import type { AtlasAtom } from "../../lib/atlas-types";
import { useSidebarSlot } from "../../design/SidebarContext";
import { brainTokens } from "../../design/brainTheme";
import { PageHeader, FilterGroup, Icon } from "../../mui";
import { type Tokens, type TooltipPayload, ageLabel, lerpColor, readTokens } from "../dashboard/atlas/atlas-canvas";
import { SemanticMap } from "../dashboard/atlas/SemanticMap";
import { ConstellationView } from "../dashboard/atlas/ConstellationView";
import { PulseView } from "../dashboard/atlas/PulseView";
import "../dashboard/atlas/Atlas.css";

const c = brainTokens.color;

const FAMILIES: { key: string; label: string; types: string[] }[] = [
  { key: "decision", label: "Decisions", types: ["decision"] },
  { key: "incident", label: "Incidents", types: ["incident", "postmortem"] },
  { key: "runbook", label: "Runbooks", types: ["runbook", "how-to"] },
  { key: "architecture", label: "Architecture", types: ["architecture", "config", "convention"] },
  { key: "note", label: "Notes", types: ["observation", "meeting-note", "client-note", "note"] },
  { key: "reference", label: "Reference", types: ["reference", "task", "idea", "workflow", "brief"] },
  { key: "person", label: "People & questions", types: ["question", "person_note"] },
];
const FAMILY_OF = new Map<string, number>();
FAMILIES.forEach((f, i) => f.types.forEach((t) => FAMILY_OF.set(t, i)));
const familyIndexOf = (type: string): number => FAMILY_OF.get(type) ?? 5;

type ColorMode = "type" | "scope" | "age" | "quality";
type Mode = "semantic" | "constellation" | "pulse";

const MODE_TABS: { key: Mode; label: string }[] = [
  { key: "semantic", label: "Semantic map" },
  { key: "constellation", label: "Constellations" },
  { key: "pulse", label: "Pulse" },
];

export function AtlasPageMui() {
  const { data, source, loading, error, reload } = useAtlasData();
  const tokens = useMemo<Tokens>(() => readTokens(), []);
  const [mode, setMode] = useState<Mode>("semantic");
  const [colorBy, setColorBy] = useState<ColorMode>("type");
  const [familiesOff, setFamiliesOff] = useState<Set<string>>(new Set());
  const [scopesOff, setScopesOff] = useState<Set<string>>(new Set());
  const [display, setDisplay] = useState({ edges: true, labels: true, superseded: true });
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [hover, setHover] = useState<TooltipPayload | null>(null);
  const [viewEpoch, setViewEpoch] = useState(0);
  const [weather, setWeather] = useState<Record<string, string> | null>(null);

  useEffect(() => {
    let live = true;
    getWeather()
      .then((byState) => {
        if (!live) return;
        const map: Record<string, string> = {};
        (Object.keys(byState) as (keyof typeof byState)[]).forEach((state) => {
          for (const node of byState[state]) map[node.nodeKey] = state;
        });
        setWeather(map);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  const atoms = useMemo(() => data?.atoms ?? [], [data]);

  const scopeOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of atoms) counts.set(a.scope, (counts.get(a.scope) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [atoms]);

  const familyCounts = useMemo(() => {
    const counts = new Array(FAMILIES.length).fill(0) as number[];
    for (const a of atoms) counts[familyIndexOf(a.type)]++;
    return counts;
  }, [atoms]);

  const visible = useMemo(() => {
    return atoms.map((a) => {
      if (familiesOff.has(FAMILIES[familyIndexOf(a.type)].key)) return false;
      if (scopesOff.has(a.scope)) return false;
      if (!display.superseded && a.status && a.status !== "active" && a.status !== "draft") return false;
      return true;
    });
  }, [atoms, familiesOff, scopesOff, display.superseded]);

  const shownCount = useMemo(() => visible.reduce((s, v) => s + (v ? 1 : 0), 0), [visible]);

  const scopeColor = useCallback(
    (scope: string): string => {
      if (scope === "company") return tokens.teal;
      if (scope.startsWith("dept:")) return TYPE_PALETTE.runbook.color;
      return TYPE_PALETTE.person_note.color;
    },
    [tokens],
  );

  const colorOf = useMemo(() => {
    const now = Date.now();
    const span = 45 * 86400000;
    switch (colorBy) {
      case "scope":
        return (a: AtlasAtom) => scopeColor(a.scope);
      case "age":
        return (a: AtlasAtom) => {
          const t = a.created_at ? Math.max(0, Math.min(1, 1 - (now - a.created_at) / span)) : 0;
          return lerpColor(tokens.tealTint, tokens.tealInk, 0.35 + 0.65 * t);
        };
      case "quality":
        return (a: AtlasAtom) => lerpColor(tokens.tealTint, tokens.tealInk, 0.35 + 0.65 * (a.quality ?? 0.4));
      default:
        return (a: AtlasAtom) => typeColor(a.type);
    }
  }, [colorBy, scopeColor, tokens]);

  const keyToIdx = useMemo(() => {
    const m = new Map<string, number>();
    atoms.forEach((a, i) => m.set(a.key, i));
    return m;
  }, [atoms]);

  const selectedAtom = selectedKey != null ? atoms[keyToIdx.get(selectedKey) ?? -1] ?? null : null;

  const related = useMemo(() => {
    if (!data || !selectedAtom) return [];
    const idx = keyToIdx.get(selectedAtom.key);
    if (idx === undefined) return [];
    return data.knn
      .filter((e) => e.a === idx || e.b === idx)
      .map((e) => ({ atom: atoms[e.a === idx ? e.b : e.a], w: e.w }))
      .sort((a, b) => b.w - a.w)
      .slice(0, 4);
  }, [data, selectedAtom, keyToIdx, atoms]);

  const onSelect = useCallback((a: AtlasAtom | null) => setSelectedKey(a?.key ?? null), []);
  const onHover = useCallback((h: TooltipPayload | null) => setHover(h), []);

  const sidebar = useMemo(
    () => (
      <>
        <FilterGroup
          label="Colour by"
          mode="single"
          selected={colorBy}
          onSelect={(k) => setColorBy(k as ColorMode)}
          rows={[
            { key: "type", label: "Atom type" },
            { key: "scope", label: "Scope" },
            { key: "age", label: "Freshness" },
            { key: "quality", label: "Quality" },
          ]}
        />
        <FilterGroup
          label="Types"
          mode="multi"
          selected={FAMILIES.filter((f) => !familiesOff.has(f.key)).map((f) => f.key)}
          onSelect={(k) =>
            setFamiliesOff((prev) => {
              const next = new Set(prev);
              if (next.has(k)) next.delete(k);
              else next.add(k);
              return next;
            })
          }
          rows={FAMILIES.map((f, i) => ({
            key: f.key,
            label: f.label,
            count: familyCounts[i],
            swatchColor: familiesOff.has(f.key) ? c.borderLight2 : typeColor(f.types[0]),
          }))}
        />
        <FilterGroup
          label="Scopes"
          mode="multi"
          selected={scopeOptions.filter(([s]) => !scopesOff.has(s)).map(([s]) => s)}
          onSelect={(k) =>
            setScopesOff((prev) => {
              const next = new Set(prev);
              if (next.has(k)) next.delete(k);
              else next.add(k);
              return next;
            })
          }
          rows={scopeOptions.map(([s, count]) => ({
            key: s,
            label: s,
            count,
            dotColor: scopesOff.has(s) ? c.borderLight2 : scopeColor(s),
          }))}
        />
        <FilterGroup
          label="Display"
          mode="multi"
          selected={Object.entries(display).filter(([, v]) => v).map(([k]) => k)}
          onSelect={(k) => setDisplay((d) => ({ ...d, [k]: !d[k as keyof typeof d] }))}
          rows={[
            { key: "edges", label: "Neighbour links", checked: display.edges },
            { key: "labels", label: "Topic labels", checked: display.labels },
            { key: "superseded", label: "Superseded atoms", checked: display.superseded },
          ]}
        />
      </>
    ),
    [colorBy, familiesOff, familyCounts, scopeOptions, scopesOff, display, scopeColor],
  );
  useSidebarSlot(sidebar);

  const eyebrow = data
    ? `${atoms.length} atoms · ${shownCount} shown${
        data.generatedAt ? ` · computed ${new Date(data.generatedAt).toLocaleDateString("en-NZ", { day: "numeric", month: "short" })}` : ""
      }`
    : "Loading the knowledge space…";

  return (
    <Box className="atlas-page" sx={{ mx: -3, my: -3, minHeight: "calc(100vh - 70px)" }}>
      <PageHeader title="Atlas" eyebrow={eyebrow} />
      <Tabs
        value={mode}
        onChange={(_, v) => {
          setMode(v as Mode);
          setHover(null);
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 1.5, minHeight: 40, "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontSize: 13.5, fontWeight: 500 } }}
      >
        {MODE_TABS.map((t) => (
          <Tab key={t.key} value={t.key} label={t.label} disableRipple />
        ))}
      </Tabs>
      <div className="atlas-stage">
        {loading && (
          <Box className="atlas-center" sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
            <CircularProgress size={28} />
            <Typography sx={{ fontSize: 13, color: c.muted }}>Projecting 1024 dimensions onto two…</Typography>
          </Box>
        )}
        {!loading && error && (
          <div className="atlas-center">
            <Icon name="alert-triangle" size={22} />
            <div style={{ maxWidth: 420 }}>{error}</div>
            <button className="atlas-btn" onClick={reload}>
              <Icon name="refresh" size={13} />
              Retry
            </button>
          </div>
        )}
        {!loading && !error && data && (
          <>
            {mode === "semantic" && (
              <SemanticMap
                key={`sem-${viewEpoch}`}
                atoms={atoms}
                visible={visible}
                knn={data.knn}
                clusters={data.clusters}
                colorOf={colorOf}
                tokens={tokens}
                showEdges={display.edges}
                showLabels={display.labels}
                selectedKey={selectedKey}
                onSelect={onSelect}
                onHover={onHover}
              />
            )}
            {mode === "constellation" && (
              <ConstellationView
                key={`con-${viewEpoch}`}
                atoms={atoms}
                visible={visible}
                weather={weather}
                colorOf={colorOf}
                tokens={tokens}
                selectedKey={selectedKey}
                onSelect={onSelect}
                onHover={onHover}
              />
            )}
            {mode === "pulse" && (
              <PulseView
                key={`pul-${viewEpoch}`}
                atoms={atoms}
                visible={visible}
                lanes={FAMILIES}
                laneIndexOf={familyIndexOf}
                colorOf={colorOf}
                tokens={tokens}
                selectedKey={selectedKey}
                onSelect={onSelect}
                onHover={onHover}
              />
            )}

            <div className="atlas-toolbar">
              {source === "snapshot" && <span className="atlas-chip" title="Served from the locally generated map-data.json snapshot">snapshot</span>}
              {(colorBy === "age" || colorBy === "quality") && (
                <span className="atlas-gradient">
                  {colorBy === "age" ? "older" : "low"}
                  <span className="atlas-gradient-bar" />
                  {colorBy === "age" ? "newer" : "high"}
                </span>
              )}
              <span className="atlas-chip">{shownCount} shown</span>
              <button className="atlas-btn" onClick={() => setViewEpoch((v) => v + 1)} title="Reset pan & zoom">
                <Icon name="focus-2" size={13} />
                Reset view
              </button>
            </div>

            {hover && !selectedAtom && <AtlasTooltip hover={hover} />}

            {selectedAtom && (
              <div className="atlas-detail" role="dialog" aria-label="Atom detail">
                <button className="atlas-detail-close" onClick={() => setSelectedKey(null)} aria-label="Close detail">
                  <Icon name="x" size={15} />
                </button>
                <div className="atlas-tooltip-meta">
                  <span className="atlas-swatch" style={{ background: typeColor(selectedAtom.type) }} />
                  {selectedAtom.type}
                  <ScopePill scope={selectedAtom.scope} />
                </div>
                <div className="atlas-detail-title">{selectedAtom.title}</div>
                <div className="atlas-detail-snippet">{selectedAtom.snippet}</div>
                {(selectedAtom.topics.length > 0 || selectedAtom.customer.length > 0 || selectedAtom.project.length > 0) && (
                  <>
                    <div className="atlas-detail-label">Facets</div>
                    <div className="atlas-facets">
                      {selectedAtom.topics.slice(0, 6).map((t) => (
                        <span key={`t-${t}`} className="atlas-facet">{t}</span>
                      ))}
                      {selectedAtom.customer.map((c) => (
                        <span key={`c-${c}`} className="atlas-facet">customer:{c}</span>
                      ))}
                      {selectedAtom.project.map((p) => (
                        <span key={`p-${p}`} className="atlas-facet">project:{p}</span>
                      ))}
                      {selectedAtom.people.slice(0, 4).map((p) => (
                        <span key={`pp-${p}`} className="atlas-facet">@{p}</span>
                      ))}
                    </div>
                  </>
                )}
                <div className="atlas-detail-label">Record</div>
                <div className="atlas-record">
                  <RecordRow k="created" v={selectedAtom.created_at ? `${new Date(selectedAtom.created_at).toLocaleDateString("en-NZ", { day: "numeric", month: "short" })} · ${ageLabel(selectedAtom.created_at)}` : "unknown"} />
                  <RecordRow k="status" v={selectedAtom.status || "active"} />
                  <RecordRow k="quality" v={selectedAtom.quality != null ? `${Math.round(selectedAtom.quality * 100)}%` : "unscored"} />
                  {selectedAtom.source && <RecordRow k="source" v={selectedAtom.source} />}
                  <RecordRow k="key" v={selectedAtom.key.slice(0, 8)} />
                </div>
                {related.length > 0 && (
                  <>
                    <div className="atlas-detail-label">Nearest in meaning</div>
                    <div className="atlas-related">
                      {related.map((r) => (
                        <button key={r.atom.key} className="atlas-related-row" onClick={() => setSelectedKey(r.atom.key)}>
                          <span className="atlas-swatch" style={{ background: typeColor(r.atom.type) }} />
                          <span style={{ flex: 1, minWidth: 0 }}>{r.atom.title}</span>
                          <span className="atlas-related-sim">{r.w.toFixed(2)}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Box>
  );
}

function RecordRow({ k, v }: { k: string; v: string }) {
  return (
    <div className="atlas-record-row">
      <span className="atlas-record-key">{k}</span>
      <span className="atlas-record-val">{v}</span>
    </div>
  );
}

function AtlasTooltip({ hover }: { hover: TooltipPayload }) {
  const style: React.CSSProperties = {
    left: hover.x + 16,
    top: hover.y + 14,
  };
  if (typeof window !== "undefined") {
    if (hover.x + 340 > window.innerWidth - 300) {
      style.left = undefined;
      style.right = `calc(100% - ${hover.x - 12}px)`;
    }
  }
  return (
    <div className="atlas-tooltip" style={style}>
      <div className="atlas-tooltip-meta">
        {hover.swatch && <span className="atlas-swatch" style={{ background: hover.swatch }} />}
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{hover.meta}</span>
      </div>
      <div className="atlas-tooltip-title">{hover.title}</div>
    </div>
  );
}
